/**
 * /operations/travel/active-travelers — Session 3B PR 18.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { Plane, Plus, Send, Sparkles, UserCheck } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { ActiveTravelerItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

export function ActiveTravelersPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const list = useQuery({
    queryKey: ["session3b-active-travelers", legalEntityId],
    queryFn: () => session3bApi.listActiveTravelers(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: ActiveTravelerItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const returningSoon = items.filter((r) => r.days_remaining <= 7);
    const destCounts = new Map<string, number>();
    for (const r of items) {
      destCounts.set(r.destination, (destCounts.get(r.destination) ?? 0) + 1);
    }
    const top = Array.from(destCounts.entries()).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      attention: returningSoon.length,
      critical: 0,
      top_destination: top?.[0],
      first_returning: returningSoon[0],
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("travel.active.title", "Active Travelers")}
          subtitle={t(
            "travel.active.subtitle",
            "Who's traveling right now. Tashghil tracks expected returns and offers Khidmat check-in nudges.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-text-tertiary">
            {t(
              "travel.active.hint",
              "Real-time view of in-flight trips. Click any traveler to open the trip detail.",
            )}
          </p>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "travel.active.cta.record_note",
                  "Record Travel on behalf — drawer with employee + dates + destination + per-diem. Uses M3X.TRAVEL HR-initiated path.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("travel.active.cta.record", "Record Travel on behalf")}
          </button>
        </div>

        {counts.attention > 0 && counts.first_returning && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<Send className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "travel.active.suggestion.returning_headline",
              `${counts.attention} traveler${counts.attention === 1 ? "" : "s"} returning within 7 days — Khidmat can stage reconciliation prompts.`,
            )}
            body={t(
              "travel.active.suggestion.returning_body",
              `${counts.first_returning.employee_name_en} returns ${counts.first_returning.expected_return_date}. Pre-staging the reconciliation form means HR doesn't chase paperwork.`,
            )}
            primaryAction={{
              label: t("travel.active.suggestion.send_checkin", "Send check-in via Khidmat"),
              onClick: () =>
                showAlertTrace(
                  t(
                    "travel.active.action.checkin_note",
                    "Khidmat will WhatsApp the traveler a check-in + reconciliation form link.",
                  ),
                ),
            }}
            suggestionKey={`travel-returning-${counts.first_returning.trip_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<UserCheck className="h-4 w-4" />}
            severity="info"
            headline={t(
              "travel.active.suggestion.empty_headline",
              "No active travelers.",
            )}
            body={t(
              "travel.active.suggestion.empty_body",
              "Tashghil notes: when employees travel for work, this surface tracks them automatically from the moment they hit 'travelling' state.",
            )}
            primaryAction={{
              label: t("travel.active.suggestion.travel_module", "Travel module"),
              onClick: () => window.location.assign("/operations/transactions/travel"),
            }}
            suggestionKey="travel-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("travel.active.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("travel.active.col.destination", "Destination")}</th>
                  <th className="px-3 py-2">{t("travel.active.col.departure", "Departure")}</th>
                  <th className="px-3 py-2">{t("travel.active.col.return", "Expected return")}</th>
                  <th className="px-3 py-2">{t("travel.active.col.days_remaining", "Days remaining")}</th>
                  <th className="px-3 py-2">{t("travel.active.col.purpose", "Purpose")}</th>
                  <th className="px-3 py-2">{t("travel.active.col.per_diem", "Per diem")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.trip_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-text-secondary">
                        <Plane className="h-3 w-3" />
                        {row.destination}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.departure_date}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.expected_return_date}</span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "travel.active.trace.days",
                              `days_remaining = expected_return_date − today. Per M3X.TRAVEL convention, the trip stays in 'travelling' state until HR records the return.`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            row.days_remaining <= 3
                              ? "bg-rose-100 text-rose-800"
                              : row.days_remaining <= 7
                                ? "bg-amber-100 text-amber-800"
                                : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {row.days_remaining}d
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-tertiary">{row.purpose}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        {row.per_diem_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("travel.active.title", "Active Travelers"),
            window_label: counts.top_destination
              ? `top destination: ${counts.top_destination}`
              : t("travel.active.window_label_empty", "no destinations"),
          }}
        />
      </aside>
    </div>
  );
}
